import { MantineStyleProps } from '@mantine/core';
import { DynamicUiEntitiesMetadata } from 'src/UI/dynamicUI/types';
import {
  BaseFinancialItem,
  FinancialAsset,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import {
  FinancialItemGenericTable,
  formatDate,
  getFinancialItemType,
} from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { formatCurrency } from 'src/cashflows/utils';

type AccountsTableProps = {
  financialItems: FinancialAsset[];
  metadata: DynamicUiEntitiesMetadata;
  pl?: MantineStyleProps['pl'];
  pr?: MantineStyleProps['pr'];
  titleParts?: string[];
  onSubmit: () => void;
  onRowClick: (clickedItem: BaseFinancialItem) => void;
};

export default function FinancialAssetsTable({
  financialItems,
  metadata,
  pl = 50,
  onRowClick,
}: AccountsTableProps) {
  // Only show Provider Reference Number column if there are InvestmentAndCash items
  const hasInvestmentAndCashItems = financialItems.some(
    item => item.product === 'InvestmentAndCash',
  );

  const fieldsRenderingRules = [
    {
      headerTitle: 'Description',
      sortable: true,
      sortKey: 'description',
      render: (item: FinancialAsset) =>
        item.description?.length && item.description?.length > 50
          ? item.description.slice(0, 50) + '...'
          : item.description,
    },
    {
      headerTitle: 'Provider',
      sortable: true,
      sortKey: 'provider',
      render: (item: FinancialAsset) => item.provider || '',
    },
    ...(hasInvestmentAndCashItems
      ? [
          {
            headerTitle: 'Provider Reference Number',
            sortable: true,
            sortKey: 'providerReferenceNumber',
            render: (item: FinancialAsset) =>
              item.product === 'InvestmentAndCash' && 'providerReferenceNumber' in item
                ? item.providerReferenceNumber || ''
                : '',
          },
        ]
      : []),
    {
      headerTitle: 'Type',
      sortable: true,
      sortKey: 'type',
      render: (item: FinancialAsset) => getFinancialItemType(item, metadata),
    },
    {
      headerTitle: 'Last updated',
      sortable: true,
      sortKey: 'valueDate',
      render: (item: FinancialAsset) => formatDate(item.valueDate),
    },
    {
      headerTitle: 'Current value',
      sortable: true,
      sortKey: 'value',
      render: (item: FinancialAsset) => formatCurrency(item.value),
    },
  ];

  return (
    <>
      <FinancialItemGenericTable
        financialItems={financialItems}
        fieldsRenderingRules={fieldsRenderingRules}
        pl={pl}
        onRowClick={onRowClick}
      />
    </>
  );
}
